export type JsonRequestBody = BodyInit | object | null | undefined;
export const API_FETCH_CACHE_MODE: RequestCache = "no-store";

export interface ApiEnvelope<T> {
  success: boolean;
  message: string;
  data: T;
  errors?: {
    fieldErrors?: Record<string, string[]>;
    formErrors?: string[];
  };
  details?: unknown;
}

const GENERIC_API_MESSAGES = new Set([
  "Une erreur est survenue",
  "Validation failed",
  "Internal server error"
]);

function isFormDataBody(value: JsonRequestBody): value is FormData {
  return typeof FormData !== "undefined" && value instanceof FormData;
}

function shouldSerializeJsonBody(body: JsonRequestBody) {
  if (body === undefined || body === null || isFormDataBody(body)) {
    return false;
  }

  if (
    typeof body === "string" ||
    body instanceof URLSearchParams ||
    body instanceof Blob ||
    body instanceof ArrayBuffer ||
    ArrayBuffer.isView(body)
  ) {
    return false;
  }

  return true;
}

export function prepareRequestBody(body: JsonRequestBody) {
  const serializeJsonBody = shouldSerializeJsonBody(body);
  const requestBody =
    body === undefined || body === null
      ? undefined
      : serializeJsonBody
        ? JSON.stringify(body)
        : body;

  return {
    requestBody: requestBody as BodyInit | undefined,
    serializeJsonBody
  };
}

export async function parseApiResponse<T>(response: Response): Promise<ApiEnvelope<T> | null> {
  const text = await response.text();

  if (!text) {
    return null;
  }

  return JSON.parse(text) as ApiEnvelope<T>;
}

export function getApiErrorMessage(payload: ApiEnvelope<unknown> | null, fallback = "Une erreur est survenue") {
  if (!payload) {
    return fallback;
  }

  const firstFieldError = payload.errors?.fieldErrors
    ? Object.values(payload.errors.fieldErrors).flat().find((message) => Boolean(message))
    : undefined;
  const firstFormError = payload.errors?.formErrors?.find((message) => Boolean(message));

  if (payload.message && !GENERIC_API_MESSAGES.has(payload.message)) {
    return payload.message;
  }

  if (firstFormError) {
    return firstFormError;
  }

  if (firstFieldError) {
    return firstFieldError;
  }

  if (typeof payload.details === "string" && payload.details.trim()) {
    return payload.details;
  }

  return payload.message || fallback;
}
